// src/utils/reviewerShare.js
// ------------------------------------------------------------
// Share links for the ผู้รีวิว (Reviewers) tab.
//
// A shared link reproduces what the sender is looking at: the same reviewer
// selected, and the same category filter applied. The map needs no coordinates
// in the link — Map.jsx fits itself around whatever the reviewer's items are
// (fitToPlacesKey={`reviewer-${id}`}), so the recipient lands on the same
// region automatically.
//
//   https://travy.buzz/?reviewer=42&rn=zephyrothz&rf=events
//     reviewer — user id (required)
//     rn       — display name, so the card reads correctly before
//                /api/users/:id resolves. Purely cosmetic.
//     rf       — active category filter: events | reviews | venues
//
// Home.js consumes these on mount and then strips them from the address bar.
// ------------------------------------------------------------
import { Capacitor } from '@capacitor/core';
import { Clipboard } from '@capacitor/clipboard';

export const REVIEWER_SHARE_PARAM_KEYS = ['reviewer', 'rn', 'rf'];

const VALID_FILTERS = ['events', 'reviews', 'venues'];

/**
 * Public web origin for links we hand to other people.
 * In Capacitor window.location.origin is capacitor://localhost, which is
 * useless to a friend — derive the real site instead (same rule TourGuideMap
 * uses for tour invite links).
 */
export const getAppBaseUrl = () => {
  if (Capacitor.isNativePlatform()) {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const derived = process.env.REACT_APP_BASE_URL || apiUrl.replace(/\/api\/?$/, '');
    return String(derived || '').replace(/\/+$/, '');
  }
  return String(window.location.origin || '').replace(/\/+$/, '');
};

/** reviewer + optional filter → absolute share URL (null when there's no id). */
export const buildReviewerShareUrl = (reviewer, filter = null) => {
  const id = reviewer?.id;
  if (id === null || id === undefined || id === '') return null;

  const params = new URLSearchParams();
  params.set('reviewer', String(id));
  if (reviewer?.name) params.set('rn', String(reviewer.name));
  if (filter && VALID_FILTERS.includes(filter)) params.set('rf', filter);

  return `${getAppBaseUrl()}/?${params.toString()}`;
};

/** location.search → { reviewerId, reviewerName, filter }, or null. */
export const parseReviewerShareParams = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch (err) {
    return null;
  }

  const raw = params.get('reviewer');
  if (!raw) return null;

  const reviewerId = parseInt(raw, 10);
  if (!Number.isFinite(reviewerId)) return null;

  const filter = params.get('rf');

  return {
    reviewerId,
    reviewerName: params.get('rn') || null,
    filter: VALID_FILTERS.includes(filter) ? filter : null,
  };
};

/** Capacitor clipboard on native, Clipboard API on web, execCommand otherwise. */
export const copyTextToClipboard = async (text) => {
  try {
    if (Capacitor.isNativePlatform()) {
      await Clipboard.write({ string: text });
      return true;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '-1000px';
    document.body.appendChild(textArea);
    textArea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textArea);
    return ok;
  } catch (err) {
    console.error('[reviewerShare] copy failed:', err);
    return false;
  }
};

/**
 * Native share sheet when the platform has one, clipboard otherwise.
 * @returns 'shared' | 'copied' | 'cancelled' | 'error'
 */
export const shareLink = async ({ url, title = '', text = '' }) => {
  if (!url) return 'error';

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch (err) {
      // The user dismissed the sheet — not a failure, and not something to
      // silently turn into a clipboard write.
      if (err && err.name === 'AbortError') return 'cancelled';
      console.warn('[reviewerShare] navigator.share failed, copying instead:', err);
    }
  }

  return (await copyTextToClipboard(url)) ? 'copied' : 'error';
};

export default buildReviewerShareUrl;
